if (typeof debounce === 'undefined') {
  function debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}
/**
 *  @class
 *  @function ThemeHeader
 */

if (!customElements.get('theme-header')) {
  class ThemeHeader extends HTMLElement {
    constructor() {
      super();

    }
    connectedCallback() {
      this.header_section = document.querySelector('.header-section');
      this.menu = this.querySelector('#mobile-menu');
      this.toggle = this.querySelector('.mobile-toggle-wrapper');
      this.stickyOffset = null;

      document.addEventListener('keyup', (e) => {
        if (e.code) {
          if (e.code.toUpperCase() === 'ESCAPE') {
            this.toggle.removeAttribute('open');
            this.toggle.classList.remove('active');
            this.classList.remove('menu-open');
          }
        }
      });
      
      window.addEventListener('pageshow', () => {
        if (this.toggle.hasAttribute('open')) {
          this.toggle.removeAttribute('open');
          this.toggle.classList.remove('active');
          this.classList.remove('menu-open');
          document.body.classList.remove('overflow-hidden');
          // Force Safari to repaint the closed state
          void this.toggle.offsetWidth;
        }
      });

      if (this.classList.contains('header-sticky--active')) {
        document.body.classList.add('header-sticky--active');
      }
      this.toggle.querySelector('.mobile-toggle').addEventListener('click', (e) => {
        if (this.toggle.classList.contains('active')) {
          e.preventDefault();
          document.body.classList.remove('overflow-hidden');
          this.toggle.classList.remove('active');
          this.classList.remove('menu-open');
          this.closeAnimation(this.toggle);
          // A transparent header goes solid white while the menu is open
          // (.menu-open::before). Removing the class doesn't always repaint the
          // ::before on mobile Safari, leaving the header stuck white. Force a
          // reflow so it reverts to transparent.
          void this.offsetWidth;
        } else {
          document.body.classList.add('overflow-hidden');
          this.classList.add('menu-open');
          setTimeout(() => {
            this.toggle.classList.add('active');
          });
        }
      });

      // Smooth scroll handling using rAF instead of debounce
      let ticking = false;
      const handleScroll = () => {
        if (!ticking) {
          requestAnimationFrame(() => {
            this.setStickyClass();
            this.setHeaderOffset();
            this.setHeaderHeight();
            ticking = false;
          });
          ticking = true;
        }
      };

      window.addEventListener('scroll', handleScroll, {
        passive: true
      });
      
      window.addEventListener('resize', debounce(() => {
        this.setHeaderHeight();
        this.setHeaderOffset();
        this.stickyOffset = null; // Forces recalibration on next scroll
      }, 100));

      window.dispatchEvent(new Event('scroll'));

      if (document.querySelector('.announcement-bar-section')) {
        window.addEventListener('scroll', this.setAnnouncementHeight.bind(this), {
          passive: true
        });
        window.dispatchEvent(new Event('resize'));
      }

      // Initialize ALL mobile menu toggles (for both initial and sticky blocks)
      this.querySelectorAll('.mobile-toggle-wrapper').forEach(wrapper => {
        wrapper.querySelectorAll('#mobile-menu summary').forEach(summary => {
          summary.addEventListener('click', this.onSummaryClick.bind(this));
        });
        wrapper.querySelectorAll('.parent-link-back--button').forEach(button => {
          button.addEventListener('click', this.onCloseButtonClick.bind(this));
        });
      });

      // Mobile Navigation transparent header support.
      setTimeout(() => {
        this.mobile_nav = document.querySelector('.header-mobile-navigation');
        if (this.mobile_nav && this.classList.contains('transparent--true')) {
          this.mobile_nav.classList.add('is-fixed');
        }
      }, 100);
    }
    setStickyClass() {
      if (this.classList.contains('header-sticky--active')) {
        if (this.stickyOffset === null) {
          // Fire sticky almost immediately on scroll (~1 scroll wheel turn).
          this.stickyOffset = 50;
        }
        const activate = this.stickyOffset;
        const deactivate = Math.max(0, this.stickyOffset - 20); // small hysteresis to prevent flashing
        if (this.classList.contains('is-sticky')) {
          if (window.scrollY <= deactivate) {
            this.classList.remove('is-sticky');
          }
        } else {
          if (window.scrollY > activate) {
            this.classList.add('is-sticky');
          }
        }
      }
    }
    setAnnouncementHeight() {
      const a_bar = document.querySelector('.announcement-bar-section');
      if (a_bar) {
        let h = a_bar.clientHeight;
        document.documentElement.style.setProperty('--announcement-height', h + 'px');
      }
    }
    setHeaderOffset() {
      if (!this.header_section) return;
      let h = this.header_section.getBoundingClientRect().top;
      document.documentElement.style.setProperty('--header-offset', h + 'px');
    }
    setHeaderHeight() {
      let h = this.offsetHeight; 
      document.documentElement.style.setProperty('--header-height', h + 'px');
    }
    onSummaryClick(event) {
      const summaryElement = event.currentTarget;
      const detailsElement = summaryElement.parentNode;
      const parentMenuElement = detailsElement.closest('.link-container');
      const menu = detailsElement.closest('#mobile-menu');
      
      if (menu && this.querySelector('.parent-link-back--button')) {
        menu.scrollTop = 0;
      }
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
      }, 100);
    }
    onCloseButtonClick(event) {
      event.preventDefault();
      const detailsElement = event.currentTarget.closest('details');
      this.closeSubmenu(detailsElement);
    }
    closeSubmenu(detailsElement) {
      detailsElement.classList.remove('menu-opening');
      this.closeAnimation(detailsElement);
    }
    closeAnimation(detailsElement) {
      let animationStart;

      const handleAnimation = (time) => {
        if (animationStart === undefined) {
          animationStart = time;
        }

        const elapsedTime = time - animationStart;

        if (elapsedTime < 400) {
          window.requestAnimationFrame(handleAnimation);
        } else {
          detailsElement.removeAttribute('open');
        }
      };

      window.requestAnimationFrame(handleAnimation);
    }
  }
  customElements.define('theme-header', ThemeHeader);
}

/**
 *  @class
 *  @function StickyHeader
 *  Standalone sticky header section (sections/header-sticky.liquid). It has its
 *  own mobile menu, so it needs the same hamburger / submenu wiring as
 *  ThemeHeader — but NOT the scroll/height logic (its own inline script toggles
 *  visibility, and it must not overwrite --header-height set by the main header).
 */
if (!customElements.get('sticky-header')) {
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      // header.js is loaded by the normal header, which is parsed BEFORE this
      // sticky section — so this element is already defined when the parser
      // reaches it and connectedCallback can fire before its mobile-menu
      // children exist (leaving the submenu/drill-down handlers unattached).
      // Defer init to DOMContentLoaded in that case.
      if (!this.querySelector('.mobile-toggle-wrapper')) {
        document.addEventListener('DOMContentLoaded', () => this.init(), { once: true });
        return;
      }
      this.init();
    }
    init() {
      this.toggle = this.querySelector('.mobile-toggle-wrapper');
      if (!this.toggle) return;

      const toggleButton = this.toggle.querySelector('.mobile-toggle');
      if (toggleButton) {
        toggleButton.addEventListener('click', (e) => {
          if (this.toggle.classList.contains('active')) {
            e.preventDefault();
            document.body.classList.remove('overflow-hidden');
            this.toggle.classList.remove('active');
            this.classList.remove('menu-open');
            this.closeAnimation(this.toggle);
            // Re-sync the sticky bar's show/hide state after closing; the scroll
            // lock can leave it stale and make the bar slide up on close.
            requestAnimationFrame(() => window.dispatchEvent(new Event('scroll')));
          } else {
            document.body.classList.add('overflow-hidden');
            this.classList.add('menu-open');
            setTimeout(() => {
              this.toggle.classList.add('active');
            });
          }
        });
      }

      this.querySelectorAll('.mobile-toggle-wrapper').forEach((wrapper) => {
        wrapper.querySelectorAll('#mobile-menu summary').forEach((summary) => {
          summary.addEventListener('click', this.onSummaryClick.bind(this));
        });
        wrapper.querySelectorAll('.parent-link-back--button').forEach((button) => {
          button.addEventListener('click', this.onCloseButtonClick.bind(this));
        });
      });

      document.addEventListener('keyup', (e) => {
        if (e.code && e.code.toUpperCase() === 'ESCAPE') {
          this.toggle.removeAttribute('open');
          this.toggle.classList.remove('active');
          this.classList.remove('menu-open');
          document.body.classList.remove('overflow-hidden');
        }
      });

      window.addEventListener('pageshow', () => {
        if (this.toggle.hasAttribute('open')) {
          this.toggle.removeAttribute('open');
          this.toggle.classList.remove('active');
          this.classList.remove('menu-open');
          document.body.classList.remove('overflow-hidden');
          void this.toggle.offsetWidth;
        }
      });
    }
    onSummaryClick(event) {
      const summaryElement = event.currentTarget;
      const detailsElement = summaryElement.parentNode;
      const parentMenuElement = detailsElement.closest('.link-container');
      const menu = detailsElement.closest('#mobile-menu');

      if (menu && this.querySelector('.parent-link-back--button')) {
        menu.scrollTop = 0;
      }
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
      }, 100);
    }
    onCloseButtonClick(event) {
      event.preventDefault();
      const detailsElement = event.currentTarget.closest('details');
      this.closeSubmenu(detailsElement);
    }
    closeSubmenu(detailsElement) {
      detailsElement.classList.remove('menu-opening');
      this.closeAnimation(detailsElement);
    }
    closeAnimation(detailsElement) {
      let animationStart;
      const handleAnimation = (time) => {
        if (animationStart === undefined) {
          animationStart = time;
        }
        const elapsedTime = time - animationStart;
        if (elapsedTime < 400) {
          window.requestAnimationFrame(handleAnimation);
        } else {
          detailsElement.removeAttribute('open');
        }
      };
      window.requestAnimationFrame(handleAnimation);
    }
  }
  customElements.define('sticky-header', StickyHeader);
}

/**
 *  @class
 *  @function FullMenu
 */
if (!customElements.get('full-menu')) {
  class FullMenu extends HTMLElement {
    constructor() {
      super();

      this.submenus = this.querySelectorAll('.thb-full-menu>.menu-item-has-children:not(.menu-item-has-megamenu)>.sub-menu');
    }
    connectedCallback() {
      if (!this.submenus.length) {
        return;
      }
      const _this = this;
      // resize on initial load
      document.fonts.ready.then(function() {
        window.addEventListener('resize', debounce(function() {
          _this.resizeSubMenus();
        }, 100));
      });

    }
    resizeSubMenus() {
      this.submenus.forEach((submenu) => {
        let sub_submenus = submenu.querySelectorAll(':scope >.menu-item-has-children>.sub-menu');

        sub_submenus.forEach((sub_submenu) => {
          let w = sub_submenu.offsetWidth,
            l = sub_submenu.parentElement.getBoundingClientRect().left + sub_submenu.parentElement.offsetWidth,
            total = w + l;

          if (total > window.innerWidth) {
            sub_submenu.parentElement.classList.add('left-submenu');
          } else if (sub_submenu.parentElement.classList.contains('left-submenu')) {
            sub_submenu.parentElement.classList.remove('left-submenu');
          }
        });
      });
    }
  }
  customElements.define('full-menu', FullMenu);
}